import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { Invitation, Talk, TalkFormData } from '../types';

const SessionInvitationAcceptPage: React.FC = () => {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [talks, setTalks] = useState<Talk[]>([]);
  const [selectedTalkId, setSelectedTalkId] = useState('');
  const [createNewTalk, setCreateNewTalk] = useState(false);
  const [newTalkData, setNewTalkData] = useState<TalkFormData>({
    title: '',
    summary: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId]);

  const fetchData = async () => {
    try {
      setFetchLoading(true);
      setError(null);
      const [invitationResponse, talksResponse] = await Promise.all([
        apiClient.get(`/v1/invitations/${invitationId}`),
        apiClient.get('/v1/talks')
      ]);
      setInvitation(invitationResponse.data.data);
      setTalks(talksResponse.data.data?.talks || []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error?.message || 'Failed to load data');
    } finally {
      setFetchLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!createNewTalk && !selectedTalkId) {
      errors.talk = 'Please select a talk or create a new one';
    }

    if (createNewTalk) {
      if (!newTalkData.title.trim()) {
        errors.title = 'Title is required';
      }
      if (!newTalkData.summary.trim()) {
        errors.summary = 'Summary is required';
      }
      if (!newTalkData.description.trim()) {
        errors.description = 'Description is required';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAccept = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let talkId = selectedTalkId;

      // Create new talk if needed
      if (createNewTalk) {
        const talkResponse = await apiClient.post('/v1/talks', newTalkData);
        talkId = talkResponse.data.data.talk_id;
      }

      // Accept invitation with talk ID
      await apiClient.post(`/v1/invitations/${invitationId}/accept`, {
        talk_id: talkId
      });

      navigate('/invitations');
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.response?.data?.error?.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this invitation?')) {
      return;
    }

    try {
      setLoading(true);
      await apiClient.post(`/v1/invitations/${invitationId}/reject`);
      navigate('/invitations');
    } catch (err: any) {
      console.error('Error rejecting invitation:', err);
      alert(err.response?.data?.error?.message || 'Failed to reject invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleNewTalkChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewTalkData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (fetchLoading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error || 'Invitation not found'}
        </div>
        <Link to="/invitations" className="btn btn-secondary">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Invitations
        </Link>
      </div>
    );
  }

  if (invitation.status !== 'pending') {
    return (
      <div className="container mt-4">
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>
          This invitation has already been {invitation.status}.
        </div>
        <Link to="/invitations" className="btn btn-secondary">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Invitations
        </Link>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="mb-3">
            <Link to="/invitations" className="btn btn-link text-decoration-none">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Invitations
            </Link>
          </div>

          <div className="card">
            <div className="card-header bg-white">
              <h3 className="mb-0">
                <i className="fas fa-microphone me-2"></i>
                Accept Session Invitation
              </h3>
            </div>
            <div className="card-body">
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                <strong>{invitation.inviter_name || 'An organizer'}</strong> has invited you to present a session at{' '}
                <strong>{invitation.target_name || 'an event'}</strong>.
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  {error}
                </div>
              )}

              <h5 className="mb-3">Select or Create a Talk</h5>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="talkOption"
                    id="existingTalk"
                    checked={!createNewTalk}
                    onChange={() => {
                      setCreateNewTalk(false);
                      setValidationErrors({});
                    }}
                    disabled={loading}
                  />
                  <label className="form-check-label" htmlFor="existingTalk">
                    Use an existing talk
                  </label>
                </div>
              </div>

              {!createNewTalk && (
                <div className="mb-3">
                  <select
                    className={`form-select ${validationErrors.talk ? 'is-invalid' : ''}`}
                    value={selectedTalkId}
                    onChange={(e) => {
                      setSelectedTalkId(e.target.value);
                      if (validationErrors.talk) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.talk;
                          return newErrors;
                        });
                      }
                    }}
                    disabled={loading || talks.length === 0}
                  >
                    <option value="">Select a talk...</option>
                    {talks.map(talk => (
                      <option key={talk.talk_id} value={talk.talk_id}>
                        {talk.title}
                      </option>
                    ))}
                  </select>
                  {validationErrors.talk && (
                    <div className="invalid-feedback">{validationErrors.talk}</div>
                  )}
                  {talks.length === 0 && (
                    <div className="form-text text-warning">
                      You don't have any talks yet. Create a new talk below.
                    </div>
                  )}
                </div>
              )}

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="talkOption"
                    id="newTalk"
                    checked={createNewTalk}
                    onChange={() => {
                      setCreateNewTalk(true);
                      setSelectedTalkId('');
                      setValidationErrors({});
                    }}
                    disabled={loading}
                  />
                  <label className="form-check-label" htmlFor="newTalk">
                    Create a new talk
                  </label>
                </div>
              </div>

              {createNewTalk && (
                <div className="border rounded p-3 mb-3">
                  <div className="mb-3">
                    <label htmlFor="title" className="form-label">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.title ? 'is-invalid' : ''}`}
                      id="title"
                      name="title"
                      value={newTalkData.title}
                      onChange={handleNewTalkChange}
                      placeholder="Enter talk title"
                      disabled={loading}
                    />
                    {validationErrors.title && (
                      <div className="invalid-feedback">{validationErrors.title}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="summary" className="form-label">
                      Summary <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control ${validationErrors.summary ? 'is-invalid' : ''}`}
                      id="summary"
                      name="summary"
                      rows={3}
                      value={newTalkData.summary}
                      onChange={handleNewTalkChange}
                      placeholder="Brief description"
                      disabled={loading}
                    />
                    {validationErrors.summary && (
                      <div className="invalid-feedback">{validationErrors.summary}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Full Description <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control ${validationErrors.description ? 'is-invalid' : ''}`}
                      id="description"
                      name="description"
                      rows={6}
                      value={newTalkData.description}
                      onChange={handleNewTalkChange}
                      placeholder="Detailed description"
                      disabled={loading}
                    />
                    {validationErrors.description && (
                      <div className="invalid-feedback">{validationErrors.description}</div>
                    )}
                  </div>
                </div>
              )}

              <div className="d-flex gap-2">
                <button
                  className="btn btn-success"
                  onClick={handleAccept}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-2"></i>
                      Accept Invitation
                    </>
                  )}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleReject}
                  disabled={loading}
                >
                  <i className="fas fa-times me-2"></i>
                  Reject
                </button>
                <Link to="/invitations" className="btn btn-secondary">
                  Cancel
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionInvitationAcceptPage;
